/* Message.js */
import { alpha, Box, Paper, TextField, Typography, useTheme } from '@mui/material';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { faMicrophone, faMicrophoneSlash, faVolumeMute, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Howl } from 'howler';


const Messages = forwardRef(({ tabHeight, token, game_id, BASE_URL, setGameResponse, isRecording, setIsRecording, isNarratorVoice, setNarratorVoice, isWideScreen, gameResponse }, ref) => {
    const narrativeRef = useRef(null);
    const inputRef = useRef(null);


    const isNarratorVoiceRef = useRef(isNarratorVoice);
    const [playerInput, setPlayerInput] = useState('');
    const [narrativeMessages, setNarrativeMessages] = useState([
        ...gameResponse.message_history.flatMap(message => {
            const result = [];
            if (message.player_message) {
                result.push({ message: message.player_message, isPlayer: true });
            }
            if (message.gm_message) {
                result.push({ message: message.gm_message, isPlayer: false });
            }
            return result;
        })
    ]);

    // Scroll to the bottom of the messages
    setTimeout(() => {
        if (narrativeRef.current) {
            narrativeRef.current.scrollTop = narrativeRef.current.scrollHeight;
        }
    }, 100);


    const [isSubmitting, setIsSubmitting] = useState(false);

    // Used to set focus on the input field BUT annoying on a phone!
    //
    // useEffect(() => {
    //     if (inputRef.current) {
    //         setTimeout(() => {
    //             inputRef.current.focus();
    //         }, 50);
    //     }
    // }, [isSubmitting]);


    const [mediaRecorder, setMediaRecorder] = useState(null);
    const audioChunksRef = useRef([]);
    const soundPlayerRef = useRef([]);

    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const gmBg = isDarkMode
        ? alpha('#c0b283', 0.15) // parchment tone with transparency
        : '#fff9c4';

    const playerBg = isDarkMode
        ? alpha('#1565c0', 0.25)  // A bit deeper and richer than #1876d2
        : '#e3f2fd';


    const playTTS = useCallback(
        async (message, game_id) => {
            if (!isNarratorVoiceRef.current) {
                return;
            }

            const encodedMessage = encodeURIComponent(message);
            const ttsEndpoint = `${BASE_URL}/v1/text-to-speech/${game_id}?text=${encodedMessage}&token=${token}`;

            // Stop existing audio if it's currently playing
            if (soundPlayerRef.current instanceof Howl && soundPlayerRef.current.playing()) {
                soundPlayerRef.current.stop();
            }

            soundPlayerRef.current = new Howl({
                src: [ttsEndpoint],
                format: ['mp3'], // Specify the format if necessary
                autoplay: true,
                html5: true, // Use HTML5 audio to ensure streaming works well
                onplayerror: function () {
                    soundPlayerRef.current.once('unlock', function () {
                        soundPlayerRef.current.play();
                    });
                },
                onloaderror: function (game_id, err) {
                    console.error('Error loading sound:', err);
                }
            });
            soundPlayerRef.current.play();
        },
        [BASE_URL, token]
    );



    useEffect(() => {

        const cleanup = () => {
            if (soundPlayerRef.current && typeof soundPlayerRef.current.stop === 'function') {
                soundPlayerRef.current.stop();
            }
        };
        cleanup();
        return cleanup;
    }, [playerInput, isRecording, isNarratorVoice]);

    useEffect(() => {
        isNarratorVoiceRef.current = isNarratorVoice;
    }, [isNarratorVoice]);

    const toggleNarratorVoice = () => {
        setNarratorVoice(!isNarratorVoice);
    };

    const insertNarrativeMessage = useCallback(
        async (message, isPlayer) => {
            setNarrativeMessages(prevMessages => {
                const newMessages = [...prevMessages, { message, isPlayer }];
                return newMessages.slice(-50); // Limit to 50 messages
            });

            // Scroll to the bottom of the messages
            setTimeout(() => {
                if (narrativeRef.current) {
                    narrativeRef.current.scrollTop = narrativeRef.current.scrollHeight;
                }
            }, 0);

            // Play TTS if the message is not from the player
            if (!isPlayer) {
                await playTTS(message, game_id);
            }
        },
        [game_id, playTTS] // Include `id` as a dependency if it's used inside the function
    );


    const handlePlayerInputSubmit = useCallback(async (event) => {
        // Check if 'event' is defined and if it has a 'key' property
        if (event && event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault(); // Prevent default action

            const playerMessage = event.transcription || playerInput;

            insertNarrativeMessage(playerMessage, true);
            setPlayerInput(''); // Clear input
            setIsSubmitting(true); // Indicate submission

            try {
                const messageEndpoint = `${BASE_URL}/v1/message/${game_id}/${gameResponse.checkpoint_id}`;
                const response = await fetch(messageEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        message: playerMessage,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const game_response = await response.json();

                insertNarrativeMessage(game_response.narrative_html, false);
                setGameResponse(game_response)

            } catch (error) {
                console.error("Error sending message:", error);
                insertNarrativeMessage("Error: Unable to process your message.", false);
            } finally {
                setIsSubmitting(false); // Re-enable input     

            }
        } else if (!event) {
            // Handle the case where 'handlePlayerInputSubmit' is called without an event
            console.warn("handlePlayerInputSubmit called without an event.");
        }

    }, [playerInput, BASE_URL, token, game_id, setGameResponse, insertNarrativeMessage, gameResponse.checkpoint_id]);


    const handleRecordClick = useCallback(async () => {
        if (isRecording) {
            // Stop recording
            mediaRecorder?.stop();
            setIsRecording(false);
        } else {
            // Start recording
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            setMediaRecorder(recorder);

            recorder.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            recorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const audioFile = new File([audioBlob], `${Date.now()}.webm`, { type: 'audio/webm' });

                // Handle the recorded audio
                const formData = new FormData();
                formData.append('audio_file', audioFile);

                setIsSubmitting(true);
                try {
                    const response = await fetch(`${BASE_URL}/v1/speech-to-text/${game_id}`, {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'Authorization': `Bearer ${token}`,
                        },
                        mode: 'cors', // Ensuring the request is made with CORS mode
                    });
                    const data = await response.json();

                    if (data.transcription) {

                        // Use a mock event object to pass to the handler
                        const simulatedEvent = {
                            transcription: data.transcription,
                            key: 'Enter',
                            preventDefault: () => { }
                        };
                        setPlayerInput('')
                        await handlePlayerInputSubmit(simulatedEvent);

                    }
                } catch (error) {
                    console.error('Error transcribing audio:', error);
                } finally {
                    setIsSubmitting(false);
                }


                // Clear recorded chunks
                audioChunksRef.current = [];
            };

            recorder.start();
            setIsRecording(true);
        }
    }, [mediaRecorder, BASE_URL, token, game_id, isRecording, setIsRecording, handlePlayerInputSubmit]);


    // Expose methods to parent using `useImperativeHandle`
    useImperativeHandle(ref, () => ({
        handleRecordClick,
    }), [handleRecordClick]);



    return (

        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: `calc(100vh - ${tabHeight})`,
                position: 'relative',
                paddingTop: '5px', // Adds top padding
                boxSizing: 'border-box',
            }}
        >
            {/* Message Display */}
            <Box ref={narrativeRef} sx={{
                height: tabHeight,
                borderRadius: "8px",
                padding: "10px",
                overflowY: "auto",
                marginBottom: "10px",
                flexGrow: 1,
                flexDirection: "column",
                justifyContent: "flex-end"
            }} >
                {narrativeMessages.map((msg, index) => (
                    <Paper
                        key={index}
                        elevation={2}
                        sx={{
                            p: 2,
                            mb: 1,
                            color: theme.palette.text.primary,
                            borderRadius: "12px",
                            backgroundColor: msg.isPlayer ? playerBg : gmBg,
                            textAlign: msg.isPlayer ? 'right' : 'left'
                        }}
                    >
                        <Typography
                            dangerouslySetInnerHTML={{ __html: msg.message }}
                            variant="body1"
                        />
                    </Paper>
                ))}
            </Box>

            {/* Narrator Toggle Icon in Top-Right */}
            {isWideScreen && (
                <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                    <FontAwesomeIcon
                        icon={isNarratorVoice ? faVolumeMute : faVolumeUp}
                        className="blue-icon"
                        onClick={toggleNarratorVoice}
                    />
                </Box>
            )}

            {/* Player Input */}
            <Box>
                <TextField
                    inputRef={inputRef}
                    multiline
                    fullWidth
                    placeholder={isSubmitting ? 'Waiting for reply...' : 'What do you do?'}
                    value={playerInput}
                    onChange={(e) => setPlayerInput(e.target.value)}
                    onKeyDown={handlePlayerInputSubmit}
                    disabled={isSubmitting}
                    rows={2}
                    sx={{ marginBottom: '10px' }}
                    variant="outlined"
                />
            </Box>

            {/* Record Icon in Bottom-Right */}
            {isWideScreen && (
                <Box sx={{ position: 'absolute', bottom: 15, right: 10 }}>
                    <FontAwesomeIcon
                        icon={isRecording ? faMicrophoneSlash : faMicrophone}
                        className="blue-icon"
                        onClick={handleRecordClick}
                    />
                </Box>
            )}

            {isSubmitting && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.75)', // Optional dim effect
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999, // ensure it's on top
                    }}
                >
                    <img
                        src="/loading.gif"
                        alt="Loading"
                        style={{ height: '100px', width: 'auto' }}
                    />
                </Box>
            )}

        </Box>
    );

});

export default Messages;
